import * as turf from '@turf/turf'
import { tilesForBounds, fetchTileStrict, MAX_TILES, SHARD_ZOOM } from '@/utils/map/AngelMapClient.js'
import { normalizeGroups } from '@/utils/map/shardAdapter.js'
import { highestPriorityGroup } from '@/utils/dueDiligence/groupPrecedence.js'
import { dedupeSpeciesHits } from '@/utils/dueDiligence/speciesHits.js'

function tractPolygons (cutFeatureCollection) {
  const features = cutFeatureCollection?.features ?? []
  return features.filter(f => {
    const type = f?.geometry?.type
    return type === 'Polygon' || type === 'MultiPolygon'
  })
}

function tilesForPolygons (polygonBounds, zoom, padding) {
  const byKey = new Map()
  polygonBounds.forEach(({ bbox }) => {
    tilesForBounds(bbox, zoom, padding).forEach(t => byKey.set(`${t.z}/${t.x}/${t.y}`, t))
  })
  return Array.from(byKey.values())
}

function bboxesOverlap (a, b) {
  return a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3]
}

function featureIntersectsTract (feature, polygonBounds) {
  try {
    const featureBbox = turf.bbox(feature)
    return polygonBounds.some(({ poly, bbox }) =>
      bboxesOverlap(featureBbox, bbox) && turf.booleanIntersects(feature, poly))
  } catch (e) {
    return false
  }
}

function speciesPairs (angelInfo) {
  const value = angelInfo?.species?.value ?? []
  if (!Array.isArray(value)) return []
  return value
    .map(pair => {
      const [common, scientific] = Array.isArray(pair) ? pair : []
      if (!common && !scientific) return null
      return {
        common: common ? String(common) : null,
        scientific: scientific ? String(scientific) : null
      }
    })
    .filter(Boolean)
}

export async function computeSpeciesOverlap (cutFeatureCollection) {
  const polygons = tractPolygons(cutFeatureCollection)
  if (polygons.length === 0) return []
  const polygonBounds = polygons.map(poly => ({ poly, bbox: turf.bbox(poly) }))

  const tiles = tilesForPolygons(polygonBounds, SHARD_ZOOM, 1)
  if (tiles.length === 0) return []
  if (tiles.length > MAX_TILES) {
    const err = new Error('Tract boundary is too large to compute species overlap')
    err.code = 'OVERLAP_AREA_TOO_LARGE'
    throw err
  }

  let collections
  try {
    collections = await Promise.all(tiles.map(t => fetchTileStrict(t.z, t.x, t.y)))
  } catch (e) {
    const err = new Error('Species layer tiles could not be fetched')
    err.code = 'OVERLAP_FETCH_FAILED'
    throw err
  }

  const hits = []
  const seenCodes = new Set()
  collections.forEach(fc => {
    (fc.features || []).forEach(feature => {
      if (!feature?.geometry) return
      const code = feature.id != null ? String(feature.id) : null
      if (code && seenCodes.has(code)) return
      if (!featureIntersectsTract(feature, polygonBounds)) return
      if (code) seenCodes.add(code)
      const angelInfo = feature.properties?.angelInfo ?? {}
      const entries = normalizeGroups(angelInfo.group?.value)
      const groupIds = entries.map(entry => entry.id).filter(id => id != null)
      const legacyKeys = entries.filter(entry => entry.id == null && entry.key != null).map(entry => entry.key)
      hits.push({ code, groupIds, legacyKeys, species: speciesPairs(angelInfo) })
    })
  })
  return hits
}

// A multi-group hex counts only as its highest-precedence group. A hex carrying any group unknown to
// `groupsById`, or only pre-angelId string keys, is unresolved — the layer is stale against the
// company's groups, and seeding a partial result would leave items that stop the seed from re-arming.
export function aggregateHitsByGroup (hits, groupsById) {
  const byGroup = new Map()
  const droppedSpeciesGroupIds = new Set()
  let unresolvedGroupHits = 0
  hits.forEach(hit => {
    if (hit.groupIds.length === 0 && hit.legacyKeys.length === 0) return
    if (hit.groupIds.length === 0 || hit.groupIds.some(id => !groupsById.has(id))) {
      unresolvedGroupHits++
      return
    }
    const owner = highestPriorityGroup(hit.groupIds.map(id => ({ id, key: null })), groupsById)
    if (!byGroup.has(owner.mitigationGroupId)) byGroup.set(owner.mitigationGroupId, [])
    if (!owner.requiresHabitat) {
      if (hit.species.length > 0) droppedSpeciesGroupIds.add(owner.mitigationGroupId)
      return
    }
    byGroup.get(owner.mitigationGroupId).push(...hit.species.map(sp => ({ commonName: sp.common, scientificName: sp.scientific })))
  })
  return {
    // Overlapping hexes repeat the same species — the server rejects a repeated pair outright,
    // so this has to collapse them on the canonical key before the seed goes out
    entries: Array.from(byGroup.entries()).map(([mitigationGroupId, species]) => ({
      mitigationGroupId,
      species: dedupeSpeciesHits(species).filter(sp => sp.commonName || sp.scientificName)
    })),
    droppedSpeciesGroupIds: Array.from(droppedSpeciesGroupIds),
    unresolvedGroupHits
  }
}
